// Canonización de nombres de prueba para fuentes multi-federación (motor lanedata).
// El motor ya publica nombres canónicos en castellano, pero esto es la DEFENSA del
// front: JSONs antiguos, federaciones nuevas o estilos de acta distintos ("Disc
// Masculí", "1000 METRES LLISOS", "100 m vallas") deben agruparse y mostrarse con
// el MISMO formato que usa el Ranking ("1.500m", "100 m v.", "Disco").
import { pruebaKey } from '@/lib/minimas';

// Vocabulario catalán/gallego/euskera → castellano + limpieza de género embebido
// en el nombre (el sexo viaja en su propio campo). Espejo de `nombre_canonico_prueba`
// del motor (fantasy-atletismo-engine, src/engine/pdf/chunk.py).
const TRADUCCIONES: [RegExp, string][] = [
  // '1.500 m.ll.' / '1000 METRES LLISOS' / '100 metro' (eu) / '100 m.' → 'Nm'
  [/(\d[\d.,]*)\s*(?:m\.?\s*ll\.?(?![\wà-ÿ])|metres(?:\s+llisos)?(?![\wà-ÿ])|metros(?:\s+lisos|\s+llanos)?(?![\wà-ÿ])|metro(?:ak)?(?:\s+lauak?)?(?![\wà-ÿ])|m\b\.?)/gi, '$1m'],
  [/\btanques\b|\bvalos\b|\bhesiak?\b/gi, 'vallas'],
  [/\bobstacles\b|\boztopoak?\b/gi, 'obstáculos'],
  [/\bmarxa\b|\bmartxa\b/gi, 'marcha'],
  [/\brelleus\b|\berreleboak?\b/gi, 'relevos'],
  [/\bcombinades\b|\bkonbinatuak?\b/gi, 'combinadas'],
  [/\bsalt\s+d['’]al[çc]ada\b|\bal[çc]ada\b|\b(?:altuera|garaiera)(?:\s+jauzia?)?\b/gi, 'Altura'],
  [/\bsalt\s+amb\s+perxa\b|\bperxa\b|\bp[ée]rtega\b|\bpertika(?:\s+jauzia?)?\b/gi, 'Pértiga'],
  [/\bsalt\s+de\s+llargada\b|\bllargada\b|\blonxitude\b|\bluzera(?:\s+jauzia?)?\b/gi, 'Longitud'],
  [/\btriple\s+salt\b(?!o)|\bjauzi\s+hirukoitza\b|\bhirukoitza\b/gi, 'Triple salto'],
  [/\b(?:llan[çc]ament|lanzamento)\s+d[eo]\s+/gi, 'Lanzamiento de '],
  [/\bjaurtiketa\b/gi, ''],
  [/\bpes\b|\bpisua?\b/gi, 'Peso'],
  [/\bdisc\b|\bdiskoa?\b/gi, 'Disco'],
  [/\bmartell\b|\bmartelo\b|\bmailua?\b/gi, 'Martillo'],
  [/\bjavelina\b|\bxabalina\b|\bxavelina\b/gi, 'Jabalina'],
  [/\bv[oò]rtex\b/gi, 'Vórtex'],
];

// Género en el nombre (es/ca/gl/eu, con plurales): se muestra/filtra por el campo sexo.
const GENERO_RE =
  /\b(femenin[oa]s?|feminin[oa]s?|femenines|femenins|femen[íi]|masculin[oa]s?|masculins|mascul[íi]|mujeres|hombres|mulleres|homes|dones|mixt[eoa]?s?|emakumezkoak?|emakumeak?|gizonezkoak?|gizonak?|neskak?|mutilak?|fem|masc)\.?(?![\wà-ÿ])/gi;

// Defensa ante JSON antiguo: código RFEA de bracket ('- SM'/'- U23m') y ámbito
// ('AL'/'PC', 'aire libre') pegados al final del título. El motor ya los quita en
// origen; esto evita nombres feos si un export viejo aún los trae.
const COD_RFEA_RE = /\s*-\s*(?:S|M|U\d{1,2})[MF]\s*$/i;
const AMBITO_RE = /\s+(?:AL|PC)\.?\s*$/;
const AMBITO_LARGO_RE = /\s+(?:aire\s+libre|pista\s+cubierta)\s*$/i;

/** Traduce ca/gl → es y quita el género embebido y los códigos RFEA. Devuelve un
 *  nombre "castellano crudo" apto para `pruebaKey` y legible como etiqueta. */
export function traducirPrueba(raw: string): string {
  let s = raw ?? '';
  s = s.replace(COD_RFEA_RE, '').replace(/\bmarcha\s+en\s+pista\b/i, 'marcha');
  for (const [re, rep] of TRADUCCIONES) s = s.replace(re, rep);
  s = s.replace(GENERO_RE, ' ');
  s = s.replace(/\s+/g, ' ').trim();
  s = s.replace(AMBITO_LARGO_RE, '').replace(AMBITO_RE, '');
  return s.trim();
}

// Etiquetas estilo Ranking por clave canónica reconocida.
const LABELS: Record<string, string> = {
  altura: 'Altura',
  pertiga: 'Pértiga',
  longitud: 'Longitud',
  triple: 'Triple salto',
  peso: 'Peso',
  disco: 'Disco',
  martillo: 'Martillo',
  jabalina: 'Jabalina',
  vortex: 'Vórtex',
  decatlon: 'Decatlón',
  heptatlon: 'Heptatlón',
  pentatlon: 'Pentatlón',
  hexatlon: 'Hexatlón',
  octatlon: 'Octatlón',
};

function fmtDist(d: string): string {
  // separador de miles con punto SIEMPRE ("1.500", "10.000") — toLocaleString('es-ES')
  // no agrupa los números de 4 cifras y el Ranking muestra "1.500m".
  return d.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
}

/** Etiqueta canónica (formato Ranking) para una clave de `pruebaKey`; '' si la
 *  clave no es reconocible (el llamante cae al nombre traducido). */
export function labelForPruebaKey(key: string): string {
  if (LABELS[key]) return LABELS[key];
  let m = key.match(/^(\d+)m$/);
  if (m) return `${fmtDist(m[1])}m`;
  m = key.match(/^(\d+)mv$/);
  if (m) return `${fmtDist(m[1])} m v.`;
  m = key.match(/^(\d+)mobst$/);
  if (m) return `${fmtDist(m[1])} m obst.`;
  m = key.match(/^(\d*)marcha$/);
  if (m) return m[1] ? `${fmtDist(m[1])} m marcha` : 'Marcha';
  return '';
}

export interface PruebaCanon {
  key: string; // clave de agrupación (misma que usa el Ranking: pruebaKey)
  label: string; // etiqueta para mostrar (formato Ranking; fallback: nombre traducido)
}

/** Canoniza un nombre crudo de prueba: mismo `key` ⇒ misma prueba (aunque cada
 *  federación la escriba distinto), `label` con el formato del Ranking. */
export function canonPrueba(raw: string): PruebaCanon {
  const traducido = traducirPrueba(raw);
  if (!traducido) return { key: '', label: raw?.trim() || '' };
  const key = pruebaKey(traducido);
  return { key: key || traducido.toLowerCase(), label: labelForPruebaKey(key) || traducido };
}
